import { useTranslation } from '../../../shared/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Dropdown, Empty, Grid, Input, List, Modal, Select, Space, Switch, Table, Tooltip, Typography, message } from 'antd';
import { MoreOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, SwapOutlined } from '@ant-design/icons';
import domainRegistrationService from '../../services/domainRegistrationService';
import dnsService from '../../services/dnsService';
import PageHeader from '../../../shared/components/PageHeader';
import DomainStatusTag from '../../../shared/components/DomainStatusTag';
import { formatDate, daysUntil } from '../../../shared/utils/date';
import { userPath } from '../../../shared/config/paths';
import RenewModal from './components/manage/RenewModal';
import NameserverEditor, { findZoneForDomain } from './components/manage/NameserverEditor';
import { showAuthCode } from './components/manage/authCode';
import RenewalTag from './components/manage/RenewalTag';
import { rowLink, ROW_LINK_CLASS, shouldIgnoreRowClick, openPath } from '../../../shared/utils/rowLink';

const { Text } = Typography;

const RENEWABLE = ['active', 'expired'];

const STATUS_OPTIONS = [
    { value: 'all', label: '全部状态' },
    { value: 'active', label: '正常' },
    { value: 'expiring', label: '30 天内到期' },
    { value: 'expired', label: '已过期' },
    { value: 'redemption', label: '赎回期' },
    { value: 'processing', label: '处理中' },
    { value: 'pending', label: '待激活' },
    { value: 'failed', label: '失败' },
    { value: 'transferred', label: '已转出' },
];

const inRedemption = (d) => d.status === 'redemption'
    || (d.redemption_period_ends_at && daysUntil(d.redemption_period_ends_at) > 0 && d.status !== 'active');

const ExpiryCell = ({ record }) => {
    const { t } = useTranslation();
    if (!record.expires_at) return <Text type="secondary">-</Text>;
    const days = daysUntil(record.expires_at);
    let color;
    let hint = null;
    if (days !== null && days < 0) {
        color = 'var(--ant-color-error, #ff4d4f)';
        hint = t('Expired');
    } else if (days !== null && days <= 30) {
        color = 'var(--ant-color-warning, #fa8c16)';
        hint = t('In {{days}} days', { days: days });
    }
    return (
        <Space size={6}>
            <span style={{ color, fontWeight: color ? 600 : undefined }}>{formatDate(record.expires_at)}</span>
            {hint && <Text style={{ color, fontSize: 12 }}>{hint}</Text>}
        </Space>
    );
};

const MyDomains = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [domains, setDomains] = useState([]);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [status, setStatus] = useState('all');
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [renewTargets, setRenewTargets] = useState(null);
    const [nsTarget, setNsTarget] = useState(null);
    const [busyIds, setBusyIds] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const r = await domainRegistrationService.getMyDomains();
            if (r.success) {
                const list = Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.list) ? r.data.list : []);
                setDomains(list);
            } else {
                message.error(r.error || t('Failed to load'));
            }
        } catch (e) {
            message.error(e.error || e.message || t('Failed to load'));
        } finally {
            setLoading(false);
        }
        try {
            const z = await dnsService.getDnsZones({});
            setZones(Array.isArray(z?.data) ? z.data : []);
        } catch (_) { setZones([]); }
    };

    useEffect(() => { load(); }, []);

    const withBusy = async (id, fn) => {
        if (busyIds.includes(id)) return;
        setBusyIds((p) => [...p, id]);
        try { await fn(); } finally { setBusyIds((p) => p.filter((x) => x !== id)); }
    };

    const toggleAutoRenew = (record, value) => withBusy(record.id, async () => {
        try {
            const r = await domainRegistrationService.toggleAutoRenew({ id: record.id, auto_renew: value });
            if (r.success) {
                setDomains((list) => list.map((d) => (d.id === record.id ? { ...d, auto_renew: value } : d)));
            } else {
                message.error(r.error || t('Operation failed'));
            }
        } catch (e) {
            message.error(e.error || e.message || t('Operation failed'));
        }
    });

    const toggleLock = (record) => withBusy(record.id, async () => {
        try {
            const r = await domainRegistrationService.toggleDomainLock({ id: record.id, lock: !record.domain_lock });
            if (r.success) {
                message.success(record.domain_lock ? t('Unlocked') : t('Locked'));
                load();
            } else {
                message.error(r.error || t('Operation failed'));
            }
        } catch (e) {
            message.error(e.error || e.message || t('Operation failed'));
        }
    });

    const restore = (record) => {
        Modal.confirm({
            title: t('Restore {{value}}', { value: record.domain_unicode || record.domain }),
            content: record.restore_price ? t('The redemption fee of {{restore_price}} will be deducted from your balance', { restore_price: record.restore_price }) : t('The redemption fee will be deducted from your balance'),
            okText: t('Confirm restore'),
            onOk: async () => {
                try {
                    const r = await domainRegistrationService.restoreDomain(record.id);
                    if (r.success) {
                        message.success(t('Restore request submitted'));
                        load();
                    } else {
                        message.error(r.error || t('Restore failed'));
                    }
                } catch (e) {
                    message.error(e.error || e.message || t('Restore failed'));
                }
            },
        });
    };

    const openDetail = (record, tab) => navigate(userPath(`/domain-registration/my-domains/${record.id}${tab ? `?tab=${tab}` : ''}`));

    const zoneOf = (record) => findZoneForDomain(zones, record);

    const counts = useMemo(() => {
        const out = { expiring: 0, expired: 0 };
        domains.forEach((d) => {
            const days = d.expires_at ? daysUntil(d.expires_at) : null;
            if (days !== null && days >= 0 && days <= 30 && d.status === 'active') out.expiring += 1;
            if (d.status === 'expired' || (days !== null && days < 0 && d.status === 'active')) out.expired += 1;
        });
        return out;
    }, [domains]);

    const filtered = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        return domains.filter((d) => {
            if (kw && !`${d.domain || ''} ${d.domain_unicode || ''}`.toLowerCase().includes(kw)) return false;
            if (status === 'all') return true;
            if (status === 'expiring') {
                const days = d.expires_at ? daysUntil(d.expires_at) : null;
                return days !== null && days >= 0 && days <= 30;
            }
            if (status === 'redemption') return inRedemption(d);
            return d.status === status;
        });
    }, [domains, keyword, status]);

    const selectedRenewable = useMemo(
        () => domains.filter((d) => selectedKeys.includes(d.id) && RENEWABLE.includes(d.status)),
        [domains, selectedKeys]
    );

    const renderActions = (record) => {
                const zone = zoneOf(record);
                const active = record.status === 'active';
                const menu = [
                    { key: 'detail', label: t('Details'), onClick: () => openDetail(record) },
                    active && { key: 'ns', label: t('DNS servers'), onClick: () => setNsTarget(record) },
                    active && {
                        key: 'dns',
                        label: zone ? t('DNS records') : t('Add DNS record'),
                        onClick: () => navigate(userPath(zone ? `/dns/records/${zone.id}` : '/dns/zones')),
                    },
                    active && { key: 'contacts', label: t('Contacts'), onClick: () => openDetail(record, 'contacts') },
                    active && { type: 'divider' },
                    active && { key: 'lock', label: record.domain_lock ? t('Unlock') : t('Lock'), onClick: () => toggleLock(record) },
                    active && { key: 'auth', label: t('Get transfer code'), onClick: () => showAuthCode(record) },
                ].filter(Boolean);
                return (
                    <Space size={4} wrap>
                        {RENEWABLE.includes(record.status) && (
                            <Button size="small" type="primary" onClick={() => setRenewTargets([record])}>{t('Renew')}</Button>
                        )}
                        {inRedemption(record) && (
                            <Button size="small" danger onClick={() => restore(record)}>{t('Restore')}</Button>
                        )}
                        {active && <Button size="small" onClick={() => setNsTarget(record)}>{t('DNS servers')}</Button>}
                        <Dropdown menu={{ items: menu }} trigger={['click']}>
                            <Button size="small" icon={<MoreOutlined />} />
                        </Dropdown>
                    </Space>
                );
    };

    const columns = [
        {
            title: t('Domains'),
            dataIndex: 'domain',
            render: (domain, record) => (
                <div style={{ minWidth: 0 }}>
                    <Link to={userPath(`/domain-registration/my-domains/${record.id}`)} style={{ fontWeight: 600, wordBreak: 'break-all' }}>
                        {record.domain_unicode || domain}
                    </Link>
                    {record.domain_unicode && record.domain_unicode !== domain && (
                        <div><Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{domain}</Text></div>
                    )}
                </div>
            ),
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            width: 150,
            render: (s, record) => <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}><DomainStatusTag status={s} /><RenewalTag renewal={record.renewal} /></span>,
        },
        {
            title: t('Expires at'),
            dataIndex: 'expires_at',
            width: 170,
            sorter: (a, b) => (a.expires_at ? new Date(a.expires_at).getTime() : Infinity) - (b.expires_at ? new Date(b.expires_at).getTime() : Infinity),
            render: (_, record) => <ExpiryCell record={record} />,
        },
        {
            title: <Tooltip title={t('Automatically renew using your account balance before expiration')}>{t('Auto-renewal')}</Tooltip>,
            dataIndex: 'auto_renew',
            width: 96,
            render: (v, record) => (
                <Switch
                    size="small"
                    checked={!!v}
                    loading={busyIds.includes(record.id)}
                    disabled={!RENEWABLE.includes(record.status)}
                    onChange={(val) => toggleAutoRenew(record, val)}
                />
            ),
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 230,
            render: (_, record) => renderActions(record),
        },
    ];

    const hasFilter = keyword || status !== 'all';
    const emptyText = hasFilter ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No matching domains')}>
                                <Button type="link" onClick={() => { setKeyword(''); setStatus('all'); }}>{t('Clear filters')}</Button>
                            </Empty>
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No domains yet')}>
                                <Space>
                                    <Button type="primary" onClick={() => navigate(userPath('/domain-registration/search'))}>{t('Register a domain')}</Button>
                                    <Button onClick={() => navigate(userPath('/domain-registration/transfer'))}>{t('Transfer a domain')}</Button>
                                </Space>
                            </Empty>
                        );


    return (
        <div>
            <PageHeader
                title={t('My domains')}
                extra={
                    <>
                        <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                        <Button icon={<SwapOutlined />} onClick={() => navigate(userPath('/domain-registration/transfer'))}>{t('Transfer in')}</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(userPath('/domain-registration/search'))}>{t('Register a domain')}</Button>
                    </>
                }
            />

            {(counts.expiring > 0 || counts.expired > 0) && status === 'all' && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={[
                        counts.expiring > 0 && t('{{expiring}} domains expire within 30 days', { expiring: counts.expiring }),
                        counts.expired > 0 && t('{{expired}} expired', { expired: counts.expired }),
                    ].filter(Boolean).join('，')}
                    action={<Button size="small" onClick={() => setStatus(counts.expiring > 0 ? 'expiring' : 'expired')}>{t('View')}</Button>}
                />
            )}

            <Card bordered={false}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'center' }}>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder={t('Search domains')}
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        style={{ flex: '1 1 220px', maxWidth: 320 }}
                    />
                    <Select value={status} onChange={setStatus} options={STATUS_OPTIONS.map(o => ({ ...o, label: t(o.label) }))} style={{ width: 150 }} />
                    <div style={{ flex: 1 }} />
                    {selectedKeys.length > 0 && (
                        <Space>
                            <Text type="secondary">{t('{{count}} selected', { count: selectedKeys.length })}</Text>
                            <Button
                                type="primary"
                                disabled={selectedRenewable.length === 0}
                                onClick={() => setRenewTargets(selectedRenewable)}
                            >
                                {selectedRenewable.length ? t('Bulk renewal ({{count}})', { count: selectedRenewable.length }) : t('Bulk renewal')}
                            </Button>
                            <Button onClick={() => setSelectedKeys([])}>{t('Cancel')}</Button>
                        </Space>
                    )}
                </div>

                {isMobile ? (
                    <List
                        loading={loading}
                        dataSource={filtered}
                        locale={{ emptyText }}
                        pagination={filtered.length > 20 ? { pageSize: 20, size: 'small' } : false}
                        renderItem={(record) => (
                            <List.Item className="row-link-card" style={{ display: 'block', padding: '12px 0' }} onClick={(e) => { if (!shouldIgnoreRowClick(e)) openPath(navigate, `/domain-registration/my-domains/${record.id}`, e); }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                    <Checkbox
                                        checked={selectedKeys.includes(record.id)}
                                        onChange={(e) => setSelectedKeys((k) => (e.target.checked ? [...k, record.id] : k.filter((x) => x !== record.id)))}
                                        style={{ marginTop: 2 }}
                                    />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                                            <Link to={userPath(`/domain-registration/my-domains/${record.id}`)} style={{ fontWeight: 600, wordBreak: 'break-all' }}>
                                                {record.domain_unicode || record.domain}
                                            </Link>
                                            <span style={{ display: 'inline-flex', gap: 4 }}><DomainStatusTag status={record.status} /><RenewalTag renewal={record.renewal} /></span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '6px 0 8px', fontSize: 13 }}>
                                            <ExpiryCell record={record} />
                                            <Space size={6}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>{t('Auto-renewal')}</Text>
                                                <Switch
                                                    size="small"
                                                    checked={!!record.auto_renew}
                                                    loading={busyIds.includes(record.id)}
                                                    disabled={!RENEWABLE.includes(record.status)}
                                                    onChange={(val) => toggleAutoRenew(record, val)}
                                                />
                                            </Space>
                                        </div>
                                        {renderActions(record)}
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />
                ) : (
                <Table
                    onRow={rowLink(navigate, (r) => `/domain-registration/my-domains/${r.id}`)}
                    rowClassName={() => ROW_LINK_CLASS}
                    rowKey="id"
                    columns={columns}
                    dataSource={filtered}
                    loading={loading}
                    scroll={{ x: 720 }}
                    rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
                    pagination={{ defaultPageSize: 20, showSizeChanger: true, hideOnSinglePage: true, showTotal: (item) => t('{{count}} total', { count: item }) }}
                    locale={{ emptyText }}
                />
                )}
            </Card>

            <RenewModal
                open={!!renewTargets}
                domains={renewTargets || []}
                onClose={() => setRenewTargets(null)}
                onDone={() => { setSelectedKeys([]); load(); }}
            />

            <Modal
                title={nsTarget ? t('DNS servers · {{value}}', { value: nsTarget.domain_unicode || nsTarget.domain }) : t('DNS servers')}
                open={!!nsTarget}
                onCancel={() => setNsTarget(null)}
                footer={null}
                destroyOnClose
            >
                {nsTarget && (
                    <NameserverEditor
                        domain={nsTarget}
                        zone={zoneOf(nsTarget)}
                        onCancel={() => setNsTarget(null)}
                        onSaved={() => { setNsTarget(null); load(); }}
                    />
                )}
            </Modal>
        </div>
    );
};

export default MyDomains;
